/**
 * Console entry point.
 *
 * Runs only on pages the generator marked with `data-console-view`; every docs page skips
 * straight past it. The order matters: no view fetches anything until a session exists,
 * otherwise the first render is a wall of 401s.
 */

import { requireSession } from './auth.ts';
import { initAccountMenu } from './account.ts';
import { mountApp } from './views/app.ts';
import { mountApps } from './views/apps.ts';
import { mountExtensions } from './views/extensions.ts';

export async function initConsole(): Promise<void> {
  const container = document.querySelector<HTMLElement>('[data-console-view]');
  if (!container) return;

  const view = container.dataset.consoleView;

  // Extensions has nothing behind a login yet, so it renders without waiting on a token.
  if (view === 'extensions') {
    mountExtensions(container);
    void initAccountMenu();
    return;
  }

  const token = await requireSession();
  if (!token) return; // redirecting to sign in

  void initAccountMenu();

  try {
    if (view === 'apps') await mountApps(container);
    else if (view === 'app') await mountApp(container);
  } catch (cause) {
    // Anything the views did not already handle: leave a readable message rather than a
    // skeleton that never resolves.
    container.textContent = '';
    const message = document.createElement('p');
    message.className = 'console-empty-note';
    message.textContent =
      cause instanceof Error ? cause.message : 'The console failed to load. Reload the page.';
    container.append(message);
  }
}
